import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReducedMotionSafe } from '@/shared/MotionWrappers';
import { usePanelStore } from './panelStore';
import { PanelShell } from './PanelShell';
import { useMobileWorkspaceStore } from './mobileWorkspaceStore';
import { SaveTemplateDialog, type SaveTemplatePayload } from '@/templates/SaveTemplateDialog';

const slideVariants = {
  enter: { x: '100%', opacity: 0 },
  center: { x: 0, opacity: 1, transition: { duration: 0.2, ease: 'easeOut' } },
  exit: { x: '100%', opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } },
};

const reducedSlideVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.08 } },
};

export function MobileWorkspace() {
  const { t } = useTranslation('common');
  const reduced = useReducedMotionSafe();
  const variants = reduced ? reducedSlideVariants : slideVariants;

  const stack = useMobileWorkspaceStore((s) => s.stack);
  const popPanel = useMobileWorkspaceStore((s) => s.popPanel);

  const panels = usePanelStore((s) => s.panels);
  const closePanel = usePanelStore((s) => s.closePanel);
  const patchPanelPayload = usePanelStore((s) => s.patchPanelPayload);

  const [saveTarget, setSaveTarget] = useState<SaveTemplatePayload | null>(null);

  const activePanelId = stack[stack.length - 1];
  const activePanel = panels.find((p) => p.panelId === activePanelId);

  const total = stack.length;
  const current = total;

  if (!activePanelId || !activePanel) {
    return (
      <div data-testid="mobile-workspace-empty" className="flex h-full flex-1 items-center justify-center text-center text-muted-foreground">
        <div className="space-y-2">
          <p className="text-sm">
            {t('workspace.emptyTitle', { defaultValue: 'No panels open yet.' })}
          </p>
          <p className="text-xs">
            {t('workspace.emptyHint', {
              defaultValue: 'Open a surface from the left nav or ask the agent to get started.',
            })}
          </p>
        </div>
      </div>
    );
  }

  function handleClose(id: string) {
    closePanel(id);
    popPanel();
  }

  return (
    <div data-testid="mobile-workspace" className="relative flex h-full flex-1 flex-col overflow-hidden bg-background">
      {/* Navigation chrome */}
      <div className="flex items-center gap-2 border-b border-border bg-card px-2 py-1">
        {total >= 1 && (
          <button
            type="button"
            aria-label={t('mobile.backToPanels', { defaultValue: 'Back' })}
            onClick={popPanel}
            className="flex items-center gap-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft size={16} />
            <span className="text-xs">
              {t('mobile.backToPanels', { defaultValue: 'Back' })}
            </span>
          </button>
        )}
        {total > 1 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {t('mobile.panelCount', {
              current,
              total,
              defaultValue: '{{current}} of {{total}}',
            })}
          </span>
        )}
      </div>

      {/* Animated panel surface */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activePanelId}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          className="flex flex-1 flex-col overflow-hidden"
        >
          <PanelShell
            panel={activePanel}
            onClose={handleClose}
            onPatchPayload={patchPanelPayload}
            onSaveTemplate={(cfg) => {
              const c = cfg as { surface: string; payload: unknown };
              setSaveTarget({ surface: c.surface, config: c.payload });
            }}
          />
        </motion.div>
      </AnimatePresence>

      <SaveTemplateDialog
        open={saveTarget !== null}
        onOpenChange={(o) => {
          if (!o) setSaveTarget(null);
        }}
        payload={saveTarget}
      />
    </div>
  );
}
